import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  FEEDBACK_CATEGORIES, INTERCOM_REGIONS, SUPPORT_PROVIDER_LABEL,
  type IntercomRegion, type SupportConnectionInfo, type SupportProvider, type SupportSettings, type SupportSyncLogEntry,
} from "@metrickle/schema";
import { api, useApiMutation } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtAgo } from "../lib/format";
import { track } from "../lib/track";
import { CATEGORY_LABEL } from "../pages/feedback";
import { ActionMenu, Ago, Badge, Button, CardSection, Checkbox, CodeBlock, Field, Input, Modal, SelectField, Table, TableScroll, Textarea, notifyError, toaster, useConfirm, ErrorState, Skeleton, type ActionMenuItem } from "./ui";

/**
 * Support tools as feedback sources, on the Integrations page: connect Zendesk or Intercom, set how
 * tickets are sorted and matched, and see what each webhook delivery and sync brought in.
 */

const REGION_LABEL: Record<IntercomRegion, string> = { us: "United States", eu: "Europe", au: "Australia" };
const TRIGGER_LABEL: Record<SupportSyncLogEntry["trigger"], string> = { webhook: "Webhook", sync: "Hourly sync", connect: "First import (7 days)" };

const useHelpdesk = (appId: string) => useQuery({ queryKey: ["helpdesk", appId], queryFn: () => api<SupportConnectionInfo[]>(`/apps/${appId}/helpdesk`) });

function status(c: SupportConnectionInfo): { text: string; tone: "good" | "bad" | "neutral" } {
  if (c.lastError) return { text: "Sync failing", tone: "bad" };
  if (!c.webhookSecret) return { text: "Hourly sync only", tone: "neutral" };
  if (c.lastWebhookAt) return { text: `Webhook ${fmtAgo(c.lastWebhookAt)}`, tone: "good" };
  return { text: "Waiting for first ticket", tone: "neutral" };
}

type Dialog = { kind: "connect" } | { kind: "setup"; c: SupportConnectionInfo } | { kind: "settings"; c: SupportConnectionInfo } | { kind: "log"; c: SupportConnectionInfo } | null;

export function HelpdeskSection({ appId }: { appId: string }) {
  const can = useCan(appId);
  const manage = can("trackers.manage");
  const list = useHelpdesk(appId);
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  return (
    <>
      <CardSection flush>
        {list.data?.length ? (
          <ul className="divide-y divide-line">
            {list.data.map((c) => (
              <Row key={c.id} appId={appId} c={c} manage={manage} onDialog={setDialog} />
            ))}
          </ul>
        ) : list.isPending ? (
          <div aria-busy="true">
            <Skeleton className="m-4 h-12" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : list.isError ? (
          <ErrorState title="Couldn't load support tools" error={list.error} />
        ) : (
          <p className="px-4 py-3 text-sm text-ink-2">
            Nothing connected. Tickets and conversations from Zendesk or Intercom come into Feedback, and a problem a customer writes in about takes back the task they'd just finished.
          </p>
        )}
        {manage && (
          <div className="border-t border-line px-4 py-2.5">
            <Button size="sm" onClick={() => setDialog({ kind: "connect" })}>
              <Plus size={13} aria-hidden /> Connect Zendesk or Intercom
            </Button>
          </div>
        )}
      </CardSection>

      <Modal open={dialog?.kind === "connect"} onOpenChange={(o) => !o && close()} title="Connect a support tool" description="Uses an access token you create in the tool. It's checked now, stored encrypted and never shown again.">
        {dialog?.kind === "connect" && <ConnectForm appId={appId} onDone={(c) => setDialog(c && (!c.webhookSecret || c.provider === "intercom") ? { kind: "setup", c } : null)} />}
      </Modal>
      <Modal open={dialog?.kind === "setup"} onOpenChange={(o) => !o && close()} title={dialog?.kind === "setup" ? `Set up the ${SUPPORT_PROVIDER_LABEL[dialog.c.provider]} webhook` : ""} description="So tickets arrive within seconds. Until then, Metrickle checks for new ones every hour." wide>
        {dialog?.kind === "setup" && <WebhookSetup appId={appId} c={dialog.c} onDone={close} />}
      </Modal>
      <Modal open={dialog?.kind === "settings"} onOpenChange={(o) => !o && close()} title={dialog?.kind === "settings" ? `Sorting and matching: ${dialog.c.label}` : ""} description="How tickets become feedback. Changes apply to tickets that arrive from now on." wide>
        {dialog?.kind === "settings" && <SettingsForm appId={appId} c={dialog.c} onDone={close} />}
      </Modal>
      <Modal open={dialog?.kind === "log"} onOpenChange={(o) => !o && close()} title={dialog?.kind === "log" ? `Sync log: ${dialog.c.label}` : ""} description="Webhook deliveries and syncs that brought something in, newest first." wide>
        {dialog?.kind === "log" && <SyncLog appId={appId} c={dialog.c} />}
      </Modal>
    </>
  );
}

function Row({ appId, c, manage, onDialog }: { appId: string; c: SupportConnectionInfo; manage: boolean; onDialog: (d: Dialog) => void }) {
  const s = status(c);
  const name = `${SUPPORT_PROVIDER_LABEL[c.provider]}: ${c.label}`;
  const invalidate = [["helpdesk", appId], ["feedback", appId]];
  const sync = useApiMutation(() => api<{ imported: number; matched: number; error: string | null }>(`/apps/${appId}/helpdesk/${c.id}/sync`, { method: "POST" }), invalidate);
  const remove = useApiMutation(() => api(`/apps/${appId}/helpdesk/${c.id}`, { method: "DELETE" }), invalidate);
  const { confirm, dialog } = useConfirm();

  const actions: ActionMenuItem[] = [
    { value: "log", label: "Sync log", onSelect: () => onDialog({ kind: "log", c }) },
    ...(manage
      ? [
          { value: "settings", label: "Sorting and matching", onSelect: () => onDialog({ kind: "settings", c }) },
          { value: "setup", label: "Webhook setup", onSelect: () => onDialog({ kind: "setup", c }) },
          {
            value: "sync",
            label: "Sync now",
            onSelect: () =>
              sync.mutate(undefined, {
                onSuccess: (r) =>
                  r.error
                    ? toaster.error({ title: "Sync failed", description: r.error })
                    : toaster.success({ title: r.imported ? `${r.imported} new report${r.imported === 1 ? "" : "s"}` : "Up to date", description: r.imported ? `${r.matched} matched to people in Metrickle.` : "No new tickets since the last sync." }),
                onError: notifyError,
              }),
          },
          {
            value: "remove",
            label: "Disconnect",
            danger: true,
            onSelect: async () => {
              if (await confirm({ title: `Disconnect ${SUPPORT_PROVIDER_LABEL[c.provider]}?`, description: `${c.label}. New tickets stop coming in. Reports already in Feedback stay, with their links back.`, confirmLabel: "Disconnect" }))
                remove.mutate(undefined, { onSuccess: () => track("helpdesk_disconnected", { provider: c.provider }), onError: notifyError });
            },
          },
        ]
      : []),
  ];

  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 @xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{SUPPORT_PROVIDER_LABEL[c.provider]}</div>
        <div className="truncate text-xs text-ink-3">{c.label}</div>
      </div>
      <div className="col-start-1 row-start-2 text-xs text-ink-2 @xl:col-start-auto @xl:row-start-auto">
        {c.lastSyncedAt ? <>Synced <Ago t={c.lastSyncedAt} /></> : "First import running…"}
        {c.lastError && <span className="block text-bad">{c.lastError}</span>}
      </div>
      <div className="col-start-1 row-start-3 @xl:col-start-auto @xl:row-start-auto">
        <Badge tone={s.tone}>{s.text}</Badge>
      </div>
      <ActionMenu
        label={`Actions for ${name}`}
        items={actions}
        triggerClassName="col-start-2 row-span-3 row-start-1 @xl:col-start-auto @xl:row-span-1 @xl:row-start-auto"
      />
      {dialog}
    </li>
  );
}

// --- Connect -----------------------------------------------------------------------------------

function ConnectForm({ appId, onDone }: { appId: string; onDone: (c?: SupportConnectionInfo) => void }) {
  const [provider, setProvider] = useState<SupportProvider>("zendesk");
  const [site, setSite] = useState("");
  const [region, setRegion] = useState<IntercomRegion>("us");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const connect = useApiMutation((b: Record<string, string>) => api<SupportConnectionInfo>(`/apps/${appId}/helpdesk`, { method: "POST", json: b }), [["helpdesk", appId]]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const c = await connect.mutateAsync(
        provider === "zendesk" ? { provider, site: site.trim(), email: email.trim(), token: token.trim() } : { provider, site: region, token: token.trim() },
      );
      track("helpdesk_connected", { provider, webhook: c.webhookRegistered });
      toaster.success({ title: `${SUPPORT_PROVIDER_LABEL[provider]} connected`, description: "Tickets from the last 7 days are coming into Feedback now." });
      onDone(c);
    } catch (err) {
      notifyError(err);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Support tool">
        <SelectField className="w-full" items={(["zendesk", "intercom"] as const).map((p) => ({ value: p, label: SUPPORT_PROVIDER_LABEL[p] }))} value={provider} onChange={(v) => setProvider(v as SupportProvider)} />
      </Field>
      {provider === "zendesk" ? (
        <>
          <Field label="Zendesk subdomain" hint="The acme in acme.zendesk.com.">
            <Input required autoComplete="off" maxLength={255} placeholder="acme" value={site} onChange={(e) => setSite(e.target.value)} />
          </Field>
          <Field label="Agent email" hint="The Zendesk account the API token belongs to. An admin lets Metrickle add the webhook for you.">
            <Input required type="email" autoComplete="off" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="API token" hint="Zendesk Admin Center → Apps and integrations → Zendesk API → turn on token access → Add API token.">
            <Input required type="password" autoComplete="off" minLength={8} maxLength={2048} className="font-mono" value={token} onChange={(e) => setToken(e.target.value)} />
          </Field>
        </>
      ) : (
        <>
          <Field label="Where your workspace is hosted">
            <SelectField className="w-full" items={INTERCOM_REGIONS.map((r) => ({ value: r, label: REGION_LABEL[r] }))} value={region} onChange={(v) => setRegion(v as IntercomRegion)} />
          </Field>
          <Field label="Access token" hint="Intercom Developer Hub → your app (create a private one if needed) → Authentication → Access token. It needs to read conversations and contacts.">
            <Input required type="password" autoComplete="off" minLength={8} maxLength={2048} className="font-mono" value={token} onChange={(e) => setToken(e.target.value)} />
          </Field>
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => onDone()}>Cancel</Button>
        <Button variant="primary" disabled={connect.isPending}>{connect.isPending ? "Checking…" : "Connect"}</Button>
      </div>
    </form>
  );
}

// --- Webhook setup -----------------------------------------------------------------------------

function WebhookSetup({ appId, c, onDone }: { appId: string; c: SupportConnectionInfo; onDone: () => void }) {
  const [secret, setSecret] = useState("");
  const save = useApiMutation((b: { webhookSecret: string }) => api<SupportConnectionInfo>(`/apps/${appId}/helpdesk/${c.id}`, { method: "PATCH", json: b }), [["helpdesk", appId]]);
  const zendesk = c.provider === "zendesk";
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      { webhookSecret: secret.trim() },
      {
        onSuccess: () => {
          track("helpdesk_webhook_secret", { provider: c.provider });
          toaster.success({ title: "Secret saved", description: "Deliveries are checked against it from now on." });
          onDone();
        },
        onError: notifyError,
      },
    );
  };
  if (zendesk && c.webhookRegistered) {
    return (
      <div className="space-y-3 text-sm text-ink-2">
        <p>Metrickle added a webhook named “Metrickle feedback” to Zendesk for new tickets, and checks every delivery's signature. There's nothing to set up.</p>
        <div className="flex justify-end"><Button variant="primary" onClick={onDone}>Done</Button></div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3 text-sm text-ink-2">
      {c.webhookUrl && <CodeBlock code={c.webhookUrl} label="Webhook URL" />}
      {zendesk ? (
        <ol className="list-decimal space-y-1 pl-5">
          <li>The token's account couldn't add the webhook, so an admin needs to: Admin Center → Apps and integrations → Webhooks → Create webhook.</li>
          <li>Choose “Zendesk events”, then “Ticket created”. Paste the URL above as the endpoint, method POST, format JSON.</li>
          <li>Open the new webhook, reveal its signing secret and paste it below.</li>
        </ol>
      ) : (
        <ol className="list-decimal space-y-1 pl-5">
          <li>Intercom Developer Hub → your app → Webhooks. Paste the URL above as the endpoint.</li>
          <li>Subscribe to the topic “conversation.user.created” and save.</li>
          <li>Under Basic information, copy the app's client secret and paste it below. Intercom signs every delivery with it.</li>
        </ol>
      )}
      <Field label={zendesk ? "Signing secret" : "Client secret"} hint={c.webhookSecret ? "One is saved. Paste a new one to replace it." : "Until one is saved, deliveries are refused and the hourly sync brings tickets in."}>
        <Input type="password" autoComplete="off" minLength={8} maxLength={512} className="font-mono" value={secret} onChange={(e) => setSecret(e.target.value)} />
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>{c.webhookSecret ? "Close" : "Later"}</Button>
        <Button variant="primary" disabled={secret.trim().length < 8} pending={save.isPending} pendingLabel="Saving…">Save secret</Button>
      </div>
    </form>
  );
}

// --- Settings ----------------------------------------------------------------------------------

const list = (s: string) => [...new Set(s.split(",").map((w) => w.trim().toLowerCase()).filter(Boolean))];

function SettingsForm({ appId, c, onDone }: { appId: string; c: SupportConnectionInfo; onDone: () => void }) {
  const [s, setS] = useState<SupportSettings>(c.settings);
  // Comma-separated text while editing, so typing a comma doesn't fight the parser.
  const [text, setText] = useState(() => ({
    ignore: c.settings.ignoreTags.join(", "),
    ...Object.fromEntries(c.settings.rules.flatMap((r) => [[`${r.category}:tags`, r.tags.join(", ")], [`${r.category}:keywords`, r.keywords.join(", ")]])),
  }) as Record<string, string>);
  const save = useApiMutation((b: { settings: SupportSettings }) => api<SupportConnectionInfo>(`/apps/${appId}/helpdesk/${c.id}`, { method: "PATCH", json: b }), [["helpdesk", appId]]);
  // Rules are checked in order, so keep the saved order (accessibility first by default).
  const categories = [...new Set([...c.settings.rules.map((r) => r.category), ...FEEDBACK_CATEGORIES])].filter((k) => k !== "other");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const settings: SupportSettings = {
      ...s,
      ignoreTags: list(text.ignore ?? ""),
      rules: categories.map((category) => ({ category, tags: list(text[`${category}:tags`] ?? ""), keywords: list(text[`${category}:keywords`] ?? "") })),
    };
    save.mutate({ settings }, { onSuccess: () => { track("helpdesk_settings_saved", { provider: c.provider, ai: settings.ai }); onDone(); }, onError: notifyError });
  };
  const edit = (key: string) => (e: { target: { value: string } }) => setText((t) => ({ ...t, [key]: e.target.value }));

  return (
    <form onSubmit={submit} className="space-y-5">
      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">Categories</legend>
        <p className="text-xs text-ink-3">
          A ticket tagged with one of a category's tags gets that category first. Otherwise its subject and first message are checked for the keywords, in this order. Anything left is Other. Separate words with commas.
        </p>
        {categories.map((category) => (
          <div key={category} className="grid gap-2 rounded-lg border border-line p-3 sm:grid-cols-2">
            <h3 className="text-[13px] font-medium text-ink sm:col-span-2">{CATEGORY_LABEL[category]}</h3>
            <Field label="Tags">
              <Input maxLength={2000} value={text[`${category}:tags`] ?? ""} onChange={edit(`${category}:tags`)} />
            </Field>
            <Field label="Keywords">
              <Textarea maxLength={6000} value={text[`${category}:keywords`] ?? ""} onChange={edit(`${category}:keywords`)} />
            </Field>
          </div>
        ))}
        <Field label="Skip tickets tagged" hint="Billing, sales or spam tickets that aren't product feedback.">
          <Input maxLength={2000} value={text.ignore ?? ""} onChange={edit("ignore")} />
        </Field>
        <Checkbox
          checked={s.ai && c.aiAvailable}
          disabled={!c.aiAvailable}
          onCheckedChange={(on) => setS({ ...s, ai: on })}
          hint={c.aiAvailable ? "When no tag matches, Claude reads the subject and the start of the first message (emails and numbers masked) and picks the category. Keywords are the fallback." : "Not available: this Metrickle server has no Anthropic API key."}
        >
          Classify with Claude
        </Checkbox>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ink">Who wrote in</legend>
        <Checkbox checked={s.match.externalId} onCheckedChange={(on) => setS({ ...s, match: { ...s.match, externalId: on } })} hint={`The requester's external ID in ${SUPPORT_PROVIDER_LABEL[c.provider]} is the user ID your app identifies people with.`}>
          Match by user ID
        </Checkbox>
        <Checkbox checked={s.match.email} onCheckedChange={(on) => setS({ ...s, match: { ...s.match, email: on } })} hint="Send email in identify traits and Metrickle keeps only a keyed hash of it, never the address. A ticket from the same email finds the person.">
          Match by email (hashed)
        </Checkbox>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ink">What a ticket does</legend>
        <Checkbox checked={s.revoke} onCheckedChange={(on) => setS({ ...s, revoke: on })} hint="A matched ticket (other than an idea) takes back a task the person finished in the 7 days before, like an in-app problem report. An accessibility barrier counts against the setting they were using.">
          Count as a problem report
        </Checkbox>
        <Checkbox checked={s.excerpt} onCheckedChange={(on) => setS({ ...s, excerpt: on })} hint="Off keeps only the subject. Either way the full conversation stays in your support tool, linked from the report.">
          Keep a short excerpt of the first message
        </Checkbox>
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
        <Button variant="primary" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

// --- Sync log ----------------------------------------------------------------------------------

function SyncLog({ appId, c }: { appId: string; c: SupportConnectionInfo }) {
  const log = useQuery({ queryKey: ["helpdesk-log", appId, c.id], queryFn: () => api<SupportSyncLogEntry[]>(`/apps/${appId}/helpdesk/${c.id}/log`) });
  if (log.isError) return <ErrorState title="Couldn't load the sync log" error={log.error} />;
  if (!log.data)
    return (
      <div aria-busy="true">
        <Skeleton className="h-32" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  if (!log.data.length) return <p className="text-sm text-ink-2">Nothing has come in yet. New tickets show here as they arrive.</p>;
  return (
    <TableScroll label="Sync log" className="max-h-96 overflow-y-auto">
      <Table caption={`Sync log for ${SUPPORT_PROVIDER_LABEL[c.provider]}`} flush>
        <thead>
          <tr>
            <th scope="col" className="py-1.5">When</th>
            <th scope="col" className="py-1.5">How</th>
            <th scope="col" className="py-1.5">Result</th>
          </tr>
        </thead>
        <tbody>
          {log.data.map((r) => (
            <tr key={r.id}>
              <td className="py-1.5 whitespace-nowrap text-ink-2"><Ago t={r.at} /></td>
              <td className="py-1.5 text-ink">{TRIGGER_LABEL[r.trigger]}</td>
              <td className="py-1.5">
                {r.error ? (
                  <><Badge tone="bad">Failed</Badge><span className="mt-0.5 block text-xs text-ink-3">{r.error}</span></>
                ) : (
                  <span className="text-ink-2">
                    {r.imported} new report{r.imported === 1 ? "" : "s"}, {r.matched} matched{r.ignored ? `, ${r.ignored} skipped by tag` : ""}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
